import React, { useState, useCallback, useEffect } from "react";
import {
    Grid, TextField, Button, Typography, Container, Chip, Box, CircularProgress,
    Paper,
    LinearProgress
} from "@material-ui/core";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import * as Yup from "yup";
import { Filtertableview } from "./Filtertableview";
import { DatePicker, DateTimePicker } from "formik-material-ui-pickers";
import { Field, Formik } from "formik";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useLabels, useNoteTypes } from "../../projectQueries";
import { useUsers } from "../../../common/CommonQueries";
import ApolloClient from "apollo-boost";
import gql from "graphql-tag";
import ClearIcon from '@material-ui/icons/Clear';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';

interface FilterState {
    id: string;
    subject: string[];
    type: any[];
    label: any[];
    author: any[];
    owner: any[];
    user: any[];
    projectNumber: string;
    status: string[];
}

const initialFilters: FilterState = {
    id: "", subject: [], type: [], label: [], author: [], owner: [], user: [], projectNumber: "", status: [],
};

const formatToGraphDate = (date: any) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split('T')[0];
};

const client = new ApolloClient({
    uri: "https://haveblue-django.azurewebsites.net/graphql/",
});

const NOTES_QUERY = gql`
  query Notes($filters: NoteFilterInput, $limit: Int, $offset: Int) { 
    notes(filters: $filters, limit: $limit, offset: $offset) {
      totalCount
      items {
        id
        subject
        ownerName
        noteTypeName
        labels { id name hexColor}
        dispositionComplete
        taggedUsers { username }
        project
        datetime
        lastUpdateDatetime
        username
      }
    }
  }
`;

export const ReportBuilder = () => {
    const { data: noteTypes, isLoading: isLoadingNoteTypes } = useNoteTypes();
    const { data: labels, isLoading: isLoadingLabels } = useLabels();
    const { data: users, isLoading: isLoadingUsers } = useUsers();

    const [subjectInput, setSubjectInput] = useState<string>("");
    const [filterbyauthorInput, setFilterbyauthorInput] = useState<string>("");
    const [filterbyownerInput, setFilterbyownerInput] = useState<string>("");

    const [filters, setFilters] = useState<FilterState>(initialFilters);
    const [searchData, setSearchData] = useState<any[] | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isSearching, setIsSearching] = useState(false);

    // Server-side state
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [lastFormValues, setLastFormValues] = useState<any>(null);

    const buildGraphQLFilters = (values: any, currentFilters: FilterState) => {
        const rawFilters: any = {
            startDate: formatToGraphDate(values.startDate),
            endDate: formatToGraphDate(values.endDate),
            creationDate: formatToGraphDate(values.creationDate),
            id: currentFilters.id ? Number(currentFilters.id) : null,
            subject: currentFilters.subject.length ? currentFilters.subject : null,
            noteType: currentFilters.type.length
                ? currentFilters.type.map((t: any) => t.name)
                : null,
            labels: currentFilters.label.length
                ? currentFilters.label.map((l: any) => l.name)
                : null,
            author: currentFilters.author.length ? currentFilters.author : null,
            owner: currentFilters.owner.length ? currentFilters.owner : null,
            taggedUsers: currentFilters.user.length
                ? currentFilters.user.map((u: any) => u.email)
                : null,

            // ✅ projectNumber as STRING
            projectNumber:
                currentFilters.projectNumber && currentFilters.projectNumber.trim() !== ""
                    ? currentFilters.projectNumber.trim()
                    : null,

            status: currentFilters.status.length
                ? currentFilters.status.map(s => (s === "Open" ? "false" : "true"))
                : null,
        };

        return Object.fromEntries(
            Object.entries(rawFilters).filter(([_, v]) => v !== null)
        );
    };




    const executeSearch = useCallback(async (formValues: any, pageIdx: number, size: number) => {
        if (!formValues) return;

        setIsSearching(true);
        const finalFilters = buildGraphQLFilters(formValues, filters);
        console.log("Executing search with filters:", finalFilters);
        try {
            const response = await client.query({
                query: NOTES_QUERY,
                variables: {
                    filters: finalFilters,
                    limit: size,
                    offset: pageIdx * size,
                },
                fetchPolicy: "network-only",
            });
            setSearchData(response.data?.notes?.items || []);
            setTotalCount(response.data?.notes?.totalCount || 0);
        } catch (error) {
            console.error("Error:", error);
            setSearchData([]);
        } finally {
            setIsSearching(false);
        }
    }, [filters]);

    // Handle pagination change
    useEffect(() => {
        if (lastFormValues) {
            executeSearch(lastFormValues, pagination.pageIndex, pagination.pageSize);
        }
    }, [pagination.pageIndex, pagination.pageSize, executeSearch]);


    const renderLabelChips = (value: any[], getTagProps: any) =>
        value.map((option: any, index: number) => {
            const colorhex = option?.hex_color || "#9e9e9e";

            return (
                <Chip
                    key={index}
                    label={option.name}
                    variant="outlined"
                    style={{
                        border: `1.5px solid ${colorhex}`,
                        color: option?.hex_color ? "#FFF" : "#000",
                        backgroundColor: option?.hex_color ? colorhex : "transparent",
                        fontWeight: 600,
                        margin: "2px",
                    }}
                    {...getTagProps({ index })}
                />
            );
        });


    const renderBlueChips = (value: any[], getTagProps: any, labelProperty: string = "name") =>
        value.map((option: any, index: number) => {
            // If hex_color exists, use it. Otherwise, use a neutral grey for the border.
            const colorhex = option?.hex_color || '#9e9e9e';

            return (
                <Chip
                    key={index}
                    variant="outlined"
                    label={typeof option === 'string' ? option : option[labelProperty]}
                    color="primary"
                    // style={{
                    //     border: `1.5px solid ${colorhex}`,
                    //     color: option?.hex_color ? '#FFF' : '#000', // White text for colors, Black for default
                    //     backgroundColor: option?.hex_color ? colorhex : 'transparent', // Clear if no color
                    //     fontWeight: 600,
                    //     margin: '2px'
                    // }}
                    {...getTagProps({ index })}
                />
            );
        });

    if (isLoadingLabels || isLoadingNoteTypes || isLoadingUsers) {
        return (<LinearProgress />)
    }

    return (
        <Formik
            initialValues={{ startDate: null, endDate: null, creationDate: null }}
            onSubmit={(values) => {
                const active = document.activeElement as HTMLInputElement;

                if (active?.value?.trim() && !filters.subject.includes(active.value.trim())) {
                    setFilters(prev => ({
                        ...prev,
                        subject: [...prev.subject, active.value.trim()]
                    }));
                }

                setLastFormValues(values);
                setPagination(p => ({ ...p, pageIndex: 0 }));
                executeSearch(values, 0, pagination.pageSize);
            }}

        >
            {({ submitForm, values, resetForm }) =>
            (
                <Box p={3}>
                    <Typography variant="h3" gutterBottom>Report Builder</Typography>
                    {/* Updated Instruction Section */}
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="flex-start"
                        mb={4}
                        color="primary"
                    >
                        <Box
                            display="flex"
                            alignItems="flex-start"
                            sx={{ maxWidth: '700px', textAlign: 'left' }} // Constraints width to force 2 lines
                        >
                            <InfoOutlinedIcon color="primary" style={{ marginRight: 12, fontSize: '2rem' }} />
                            <Typography variant="body1" color="primary" style={{ fontWeight: 500, fontSize: '1.1rem' }} >
                                Type keywords and press Enter to add filters. Multiple keywords can be added<br />
                                per field to refine results. Fields left blank will show all values.
                            </Typography>
                        </Box>
                    </Box>
                    <Container maxWidth="md" style={{ paddingBottom: 32 }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}>
                                <Field
                                    component={DatePicker}
                                    name="startDate"
                                    label="Start Date"
                                    format="yyyy-MM-DD"
                                    disableOnSubmit={false}
                                    disabled={false}
                                    inputVariant="outlined"
                                    fullWidth
                                    // ✅ Place these here (outside TextFieldProps)
                                    clearable={true}
                                    clearLabel="CLEAR"
                                    TextFieldProps={{
                                        placeholder: "yyyy-MM-dd",
                                    }}
                                    maxDate={new Date()}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Field
                                    component={DatePicker}
                                    name="endDate"
                                    label="End Date"
                                    format="yyyy-MM-DD"
                                    inputVariant="outlined"
                                    fullWidth
                                    disableOnSubmit={false}
                                    disabled={false}
                                    clearable
                                    clearLabel="CLEAR"
                                    TextFieldProps={{ placeholder: "yyyy-MM-dd" }}
                                    maxDate={new Date()}
                                    minDate={values.startDate || undefined}
                                />

                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Id" variant="outlined" fullWidth value={filters.id} onChange={(e) => setFilters({ ...filters, id: e.target.value })} type="number" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={filters.subject}
                                    // Track the typing progress
                                    inputValue={subjectInput}
                                    onInputChange={(_, newInputValue) => {
                                        setSubjectInput(newInputValue);
                                    }}
                                    onChange={(_, v) =>
                                        setFilters({ ...filters, subject: v as string[] })
                                    }
                                    renderTags={(val, props) => renderBlueChips(val, props)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Subject"
                                            fullWidth
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    const value = (e.target as HTMLInputElement).value.trim();

                                                    if (value && !filters.subject.includes(value)) {
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            subject: [...prev.subject, value]
                                                        }));
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = (e.target as HTMLInputElement).value.trim();

                                                if (value && !filters.subject.includes(value)) {
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        subject: [...prev.subject, value]   // ✅ ARRAY
                                                    }));
                                                }
                                            }}
                                        />
                                    )}
                                />

                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete multiple options={noteTypes?.results || []} getOptionLabel={(o: any) => o.name} value={filters.type} onChange={(_, v) => setFilters({ ...filters, type: v })} renderTags={(val, props) => renderBlueChips(val, props)} renderInput={(p) => <TextField {...p} variant="outlined" label="Filter by Type" />} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete multiple options={labels?.results || []} getOptionLabel={(o: any) => o.name} value={filters.label} onChange={(_, v) => setFilters({ ...filters, label: v })} renderTags={(val, props) => renderLabelChips(val, props)} renderOption={(option: any) => (
                                    <Grid container alignItems="center">
                                        <Box
                                            style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: option.hex_color || '#ccc',
                                                marginRight: 8
                                            }}
                                        />
                                        <Typography variant="body2">{option.name}</Typography>
                                    </Grid>
                                )}
                                    renderInput={(p) => <TextField {...p} variant="outlined" label="Filter by Label" />} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {/* <TextField label="Author" variant="outlined" fullWidth value={filters.author} onChange={(e) => setFilters({ ...filters, author: e.target.value })} /> */}
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={filters.author}
                                    // Track the typing progress
                                    inputValue={filterbyauthorInput}
                                    onInputChange={(_, newInputValue) => {
                                        setFilterbyauthorInput(newInputValue);
                                    }}
                                    onChange={(_, v) =>
                                        setFilters({ ...filters, author: v as string[] })
                                    }
                                    renderTags={(val, props) => renderBlueChips(val, props)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Author"
                                            fullWidth
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    const value = (e.target as HTMLInputElement).value.trim();

                                                    if (value && !filters.author.includes(value)) {
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            author: [...prev.author, value]
                                                        }));
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = (e.target as HTMLInputElement).value.trim();

                                                if (value && !filters.subject.includes(value)) {
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        author: [...prev.author, value]
                                                    }));
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={filters.owner}
                                    inputValue={filterbyownerInput}
                                    onInputChange={(_, newInputValue) => {
                                        setFilterbyownerInput(newInputValue);
                                    }}
                                    onChange={(_, v) =>
                                        setFilters({ ...filters, owner: v as string[] })
                                    }
                                    renderTags={(val, props) => renderBlueChips(val, props)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Owner"
                                            fullWidth
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    const value = (e.target as HTMLInputElement).value.trim();

                                                    if (value && !filters.owner.includes(value)) {
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            owner: [...prev.owner, value]
                                                        }));
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = (e.target as HTMLInputElement).value.trim();

                                                if (value && !filters.owner.includes(value)) {
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        owner: [...prev.owner, value]
                                                    }));
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Autocomplete multiple options={users || []} getOptionLabel={(o: any) => o.email} value={filters.user} onChange={(_, v) => setFilters({ ...filters, user: v })} renderTags={(val, props) => renderBlueChips(val, props, "email")} renderInput={(p) => <TextField {...p} variant="outlined" label="Select a User to filter" />} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Field
                                    component={DatePicker}
                                    name="creationDate"
                                    label="Creation Date"
                                    format="yyyy-MM-DD"
                                    inputVariant="outlined"
                                    fullWidth
                                    disableOnSubmit={false}
                                    disabled={false}
                                    clearable
                                    clearLabel="CLEAR"
                                    maxDate={
                                        values.endDate
                                            ? values.endDate
                                            : new Date()
                                    }
                                    minDate={
                                        values.startDate
                                            ? values.startDate
                                            : undefined
                                    }
                                    TextFieldProps={{ placeholder: "yyyy-MM-dd" }}
                                />


                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Project Number" variant="outlined" fullWidth value={filters.projectNumber} onChange={(e) => setFilters({ ...filters, projectNumber: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete multiple options={["Open", "Closed"]} value={filters.status} onChange={(_, v) => setFilters({ ...filters, status: v as string[] })} renderTags={(val, props) => renderBlueChips(val, props)} renderInput={(p) => <TextField {...p} variant="outlined" label="Status" />} />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} justify="center" style={{ marginTop: 48 }}>
                            <Grid item>
                                <Button variant="contained" color="primary" onClick={submitForm} disabled={isSearching}>
                                    {isSearching ? <CircularProgress size={20} color="inherit" /> : "SEARCH"}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        setFilters(initialFilters);
                                        setSubjectInput("");
                                        setSearchData(null);
                                        setTotalCount(0);
                                        setLastFormValues(null);
                                        setPagination({ pageIndex: 0, pageSize: 10 });
                                        resetForm();
                                    }}
                                >
                                    CLEAR ALL
                                </Button>
                            </Grid>
                        </Grid>
                    </Container>

                    {searchData && (
                        <Box mt={4}>
                            {totalCount === 0 && !isSearching ? (
                                <Paper
                                    elevation={0}
                                    style={{
                                        padding: '60px 20px',
                                        textAlign: 'center',
                                        backgroundColor: '#fafafa',
                                        border: '1px dashed #d0d0d0',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Box mb={2}>
                                        <InfoOutlinedIcon style={{ fontSize: 64, color: '#bdbdbd' }} />
                                    </Box>
                                    <Typography variant="h5" style={{ fontWeight: 500, color: '#616161' }} gutterBottom>
                                        No results found
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" style={{ maxWidth: 400, margin: '0 auto' }}>
                                        We couldn't find any notes matching your current filters.

                                    </Typography>

                                </Paper>
                            ) : (
                                <Filtertableview
                                    Data={searchData}
                                    totalCount={totalCount}
                                    loading={isSearching}
                                    fetchData={setPagination}
                                    formValues={lastFormValues}
                                    filters={filters}
                                    query={NOTES_QUERY}
                                />
                            )}
                        </Box>
                    )}

                </Box>
            )}
        </Formik>
    );
};